import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = 'https://api.fluidstream.co';

export default function UnitDetail({ auth, onLogout }) {
  const { unitId } = useParams();
  const navigate   = useNavigate();
  const [data,     setData]    = useState(null);
  const [history,  setHistory] = useState([]);
  const [loading,  setLoading] = useState(true);
  const [error,    setError]   = useState('');

  const headers = { Authorization: `Bearer ${auth.token}` };

  useEffect(() => {
    fetchLatest();
    fetchHistory();
    const interval = setInterval(fetchLatest, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line
  }, [unitId]);

  const fetchLatest = async () => {
    try {
      const res = await axios.get(`${API}/units/${unitId}/latest`, { headers });
      setData(res.data);
      setError('');
    } catch {
      setError('Failed to load unit data');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(
        `${API}/units/${unitId}/history?range=1h&index=0&type=real`,
        { headers }
      );
      setHistory(res.data.map(p => ({
        time:  new Date(p.time).toLocaleTimeString(),
        value: parseFloat(p.value.toFixed(2))
      })));
    } catch {}
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button style={styles.backBtn} onClick={() => navigate('/')}>Back</button>
          <img src="/logo-white.png" alt="Fluidstream" style={styles.logoImg} />
          <div style={styles.headerDivider} />
          <div>
            <p style={styles.headerLabel}>Unit Detail</p>
            <p style={styles.headerUnit}>{unitId}</p>
          </div>
        </div>
        <button style={styles.logoutBtn} onClick={onLogout}>Sign Out</button>
      </div>

      <div style={styles.content}>
        {loading && <div style={styles.message}>Loading...</div>}
        {error   && <div style={styles.errorMsg}>{error}</div>}

        {data && (
          <>
            <h2 style={styles.sectionTitle}>Process Values</h2>
            <div style={styles.grid}>
              {Object.entries(data.real).map(([index, value]) => (
                <div key={index} style={styles.valueCard}>
                  <div style={styles.valueLabel}>Real [{index}]</div>
                  <div style={styles.valueNumber}>{Number(value).toFixed(2)}</div>
                </div>
              ))}
            </div>

            <h2 style={styles.sectionTitle}>Status Bits</h2>
            <div style={styles.grid}>
              {Object.entries(data.bool).map(([index, value]) => (
                <div key={index} style={styles.statusCard}>
                  <div style={styles.valueLabel}>Bool [{index}]</div>
                  <div style={{...styles.statusBadge, background: value ? '#1a73b8' : '#ccc'}}>
                    {value ? 'TRUE' : 'FALSE'}
                  </div>
                </div>
              ))}
            </div>

            {Object.keys(data.alarm).length > 0 && (
              <>
                <h2 style={styles.sectionTitle}>Alarm Words</h2>
                <div style={styles.grid}>
                  {Object.entries(data.alarm).map(([index, value]) => (
                    <div key={index} style={{...styles.valueCard, borderLeft: value > 0 ? '4px solid #cc0000' : '4px solid #1a73b8'}}>
                      <div style={styles.valueLabel}>Alarm [{index}]</div>
                      <div style={styles.valueNumber}>{value}</div>
                      <div style={styles.valueSub}>{value > 0 ? 'Active alarms present' : 'No alarms'}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {history.length > 0 && (
              <>
                <h2 style={styles.sectionTitle}>Real [0] - Last Hour</h2>
                <div style={styles.chartCard}>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={history}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef4fb" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="value" stroke="#1a73b8" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page:          { minHeight: '100vh', background: '#EEF4FB' },
  header:        { background: '#1a2a4a', padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerLeft:    { display: 'flex', alignItems: 'center', gap: 14 },
  backBtn:       { padding: '6px 14px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.3)', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 13 },
  logoImg:       { height: 28, width: 'auto' },
  headerDivider: { width: 1, height: 36, background: 'rgba(255,255,255,0.2)' },
  headerLabel:   { fontSize: 10, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1.5 },
  headerUnit:    { fontSize: 14, color: '#fff', fontWeight: 600, marginTop: 2 },
  logoutBtn:     { padding: '8px 18px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.3)', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 14 },
  content:       { padding: '24px 32px' },
  message:       { textAlign: 'center', color: '#888', padding: 40 },
  errorMsg:      { background: '#fff0f0', border: '1px solid #ffcccc', borderRadius: 8, padding: 16, color: '#cc0000', marginBottom: 16 },
  sectionTitle:  { fontSize: 15, fontWeight: 600, color: '#1a2a4a', marginBottom: 12, marginTop: 24, paddingBottom: 8, borderBottom: '2px solid #dce8f5' },
  grid:          { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 },
  valueCard:     { background: '#fff', borderRadius: 10, padding: 16, boxShadow: '0 2px 8px rgba(26,115,180,0.08)', borderLeft: '4px solid #1a73b8' },
  statusCard:    { background: '#fff', borderRadius: 10, padding: 16, boxShadow: '0 2px 8px rgba(26,115,180,0.08)' },
  valueLabel:    { fontSize: 11, color: '#999', marginBottom: 6, fontFamily: 'monospace' },
  valueNumber:   { fontSize: 24, fontWeight: 700, color: '#1a2a4a' },
  valueSub:      { fontSize: 11, color: '#888', marginTop: 4 },
  statusBadge:   { display: 'inline-block', padding: '4px 12px', borderRadius: 20, color: '#fff', fontSize: 13, fontWeight: 600, marginTop: 4 },
  chartCard:     { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(26,115,180,0.08)' },
};
